import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import KeyIcon from '@mui/icons-material/Key';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StorageIcon from '@mui/icons-material/Storage';

interface DemoMessage {
  sender: 'pmo' | 'user' | 'agent';
  agentRole?: string;
  content: string;
}

interface DemoScenario {
  id: string;
  title: string;
  category: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  tags: string[];
  messages: DemoMessage[];
}

const DEMO_SCENARIOS: DemoScenario[] = [
  {
    id: 'ds-1',
    title: 'SSH 総当たり攻撃検知',
    category: 'セキュリティ',
    icon: <SecurityIcon />,
    color: '#E12D39',
    description: 'GuardDuty からの重大度 High アラートを受信し、攻撃元 IP の遮断とインスタンス隔離を実施。',
    tags: ['AWS', 'GuardDuty', 'インシデント対応'],
    messages: [
      { sender: 'pmo', content: 'GuardDutyより重大度HighのSSH総当たり攻撃アラートを受信しました。直ちに調査を開始してください。' },
      { sender: 'user', content: '対象のEC2インスタンスと攻撃元IPを特定し、即時対応策を提案して。' },
      { sender: 'agent', agentRole: 'Security Agent', content: '対象リソース: i-0abcd1234efgh5678 (Web-Prod-01)\n攻撃元IP: 203.0.113.5 (Malicious Actor)\n\n推奨対応:\n1. 攻撃元IPのNetwork ACLおよびSecurity Groupでのブロック\n2. 対象インスタンスの隔離およびスナップショット取得\n3. IAMロールの一時的無効化' },
      { sender: 'agent', agentRole: 'Log Agent', content: 'CloudTrailログ分析結果: 当該IPからのAPIコールは確認されませんでした。コンソールへの不正ログインの痕跡はありません。' },
    ],
  },
  {
    id: 'ds-2',
    title: 'API レスポンス遅延 SLO 違反',
    category: 'パフォーマンス',
    icon: <SpeedIcon />,
    color: '#38BEC9',
    description: 'p99 レイテンシが 2,450ms に到達。デプロイ後の N+1 クエリとキャッシュミスが原因。MTTR 12分で復旧。',
    tags: ['AWS', 'SLO', 'RDS', 'Redis'],
    messages: [
      { sender: 'pmo', content: 'CloudWatch Alarm "prod-api-latency-p99" が ALARM 状態に遷移。p99レイテンシが 2,450ms。SLO 500ms を大幅超過。\n\nランブック RB-AWS-001 を自動起動しました。' },
      { sender: 'user', content: 'ボトルネックの原因を特定して、復旧手順を実行してください。' },
      { sender: 'agent', agentRole: 'Performance Agent', content: '根本原因: RDS CPU 98.2%\n起点: 2/20 デプロイ (feature/new-search-algorithm)\nRedis キャッシュヒット率 85% → 12% に低下。キャッシュキー命名規則変更が原因。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: 'API遅延によるコンバージョン率低下: 推定 ¥800万/日 の売上損失\nスケールアップコスト: +¥150,000/月\n推奨: まずアプリ側修正で根本対応。' },
      { sender: 'agent', agentRole: 'Performance Agent', content: '復旧完了: レスポンスタイム 180ms (SLO: 500ms)\nMTTR: 12分\nエラーバジェット: 残り 12.96分/43.2分 (70% 消費)' },
    ],
  },
  {
    id: 'ds-3',
    title: 'GPU インスタンス停止忘れ → AI 自動起票',
    category: 'コスト',
    icon: <AttachMoneyIcon />,
    color: '#57AE5B',
    description: 'Cost Anomaly Detection が EC2 コスト 134% 増を検知。AI が起票文を自動生成し、チケットシステムに連携。',
    tags: ['AWS', 'FinOps', 'AI起票', 'Trusted Advisor'],
    messages: [
      { sender: 'pmo', content: 'AWS Cost Anomaly Detection からアラート。EC2 利用料金が前日比 134% 増加。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '根本原因: developer-yamamoto が p3.8xlarge × 4台を起動し、学習完了後も停止忘れ。48時間アイドル。\n超過額: ¥510,000/日 (月換算 ¥15,300,000)' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '【AI 自動起票文】\n■ EC2 コスト異常 — GPU停止忘れ\n■ 推奨: 即時停止 + GPU自動停止ルール導入 + 起動時承認フロー' },
      { sender: 'user', content: 'チケットを作成して、developer-yamamoto にアサインしてください。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '✅ チケット COST-2026-0215 作成、Slack #sre-alerts に通知済み。' },
    ],
  },
  {
    id: 'ds-4',
    title: 'IAM 過剰権限 — 四半期監査',
    category: 'IAM',
    icon: <KeyIcon />,
    color: '#724BB7',
    description: '外部委託者の未削除アカウント、MFA 未設定 29 名を検出。AI が最小権限ポリシーを自動生成。',
    tags: ['AWS', 'Azure', 'コンプライアンス', 'MFA'],
    messages: [
      { sender: 'pmo', content: '四半期IAM監査の実施時期です。全アカウントの権限棚卸しを開始してください。' },
      { sender: 'agent', agentRole: 'IAM Agent', content: 'Critical 3件検出:\n1. contractor-suzuki — 90日以上未使用\n2. developer-tanaka — S3管理者 + MFA未設定\n3. lambda-data-processor — ワイルドカード権限\n\nコンプライアンススコア: 84.9% (目標: 95%)' },
      { sender: 'agent', agentRole: 'IAM Agent', content: 'developer-tanaka 向け最小権限ポリシー自動生成: 892個 → 3個に削減' },
    ],
  },
  {
    id: 'ds-5',
    title: 'Lambda 無限リトライ → コスト 611% 増',
    category: 'ログ分析',
    icon: <SearchIcon />,
    color: '#F0B429',
    description: 'Lambda 関数の無限ループにより 120 万回/日 の異常実行。ログ相関分析でコスト異常と連動。',
    tags: ['AWS', 'Lambda', 'DynamoDB', 'コスト異常'],
    messages: [
      { sender: 'pmo', content: 'Lambda 利用料金が前日比 611% の異常急増。関連ログの調査を開始。' },
      { sender: 'agent', agentRole: 'Log Agent', content: 'Lambda "data-sync-handler" — 通常 50,000回/日 → 1,200,000回/日\nDynamoDB ProvisionedThroughputExceededException で無限リトライ。backoff なし。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '24時間超過コスト: ¥330,000\nLambda + DynamoDB 合計: ¥405,000/日 (通常 ¥75,000/日)' },
      { sender: 'agent', agentRole: 'Log Agent', content: '✅ Lambda 同時実行数 0 に設定（緊急停止）\n⏳ 開発チームにバグ修正を依頼' },
    ],
  },
  {
    id: 'ds-6',
    title: 'FinOps 月次レポート + Trusted Advisor',
    category: '統合レポート',
    icon: <AssessmentIcon />,
    color: '#2680C2',
    description: '月次コスト ¥12.8M (予算比 128%)。Trusted Advisor + AI 分析で年間 ¥23.1M の最適化余地を提案。',
    tags: ['AWS', 'Azure', 'FinOps', 'Trusted Advisor', 'ROI'],
    messages: [
      { sender: 'user', content: '今月のFinOpsレポートを作成してください。Trusted Advisor の結果と合わせて。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '当月実績: ¥12,800,000 (予算 ¥10,000,000, 128%)\nAWS: ¥8,200,000 | Azure: ¥4,600,000\n\nコスト増加 Top 3:\n1. GPU停止忘れ (+¥480,000/日)\n2. Lambda無限ループ (+¥330,000)\n3. Azure SQL Auto-scale未戻し (+¥78,000/月)' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '月次最適化提案:\n1. 開発環境 夜間/週末停止: ¥980,000/月\n2. RDS レプリカ統合: ¥450,000/月\n3. S3 Glacier: ¥320,000/月\n4. 未使用リソース削除: ¥180,000/月\n合計: ¥1,930,000/月 (年間 ¥23,160,000, ROI 4,532%)' },
    ],
  },
  {
    id: 'ds-7',
    title: 'Databricks ETL ジョブ OOM → 下流 SLO 違反',
    category: 'Databricks',
    icon: <StorageIcon />,
    color: '#ff6b35',
    description: 'ML 特徴量生成ジョブが OOM で失敗。キャンペーンによるデータ量 3 倍増が原因。下流 3 テーブルの Freshness SLO 違反。',
    tags: ['Databricks', 'Spark', 'OOM', 'Data Freshness', 'SLO'],
    messages: [
      { sender: 'pmo', content: 'Databricks ジョブ監視アラート: ml-feature-generation が FAILED\nエラー: java.lang.OutOfMemoryError: Java heap space\nタスク compute_features が OOM で失敗。' },
      { sender: 'user', content: 'OOM の原因を特定して、復旧と再実行を。下流への影響も確認して。' },
      { sender: 'agent', agentRole: 'Performance Agent', content: '根本原因: 入力データ量 15GB → 45GB (3倍増、キャンペーン施策影響)\nExecutor Memory: 16GB (不足)\nShuffle Spill: 2GB → 38GB, GC Time: 5% → 42%\n\n推奨: spark.executor.memory 32g + AQE 有効化' },
      { sender: 'agent', agentRole: 'Cost Agent', content: 'リサイズコスト比較:\nA. メモリ増量: +$180/月 (推奨)\nB. ワーカー追加: +$360/月\nC. Spot利用: +$108/月' },
      { sender: 'agent', agentRole: 'Log Agent', content: '下流 Data Freshness SLO 影響:\n- churn_prediction_features: 195分遅延 (SLO 120分) SEV-3\n- customer_360: 195分遅延 (SLO 60分) SEV-2\n- marketing_segments: 195分遅延 (SLO 180分) SEV-3\n\n復旧後すべて正常復帰を確認。' },
    ],
  },
  {
    id: 'ds-8',
    title: 'Databricks DBU コスト異常 — 72h アイドルクラスター',
    category: 'Databricks',
    icon: <StorageIcon />,
    color: '#ff6b35',
    description: '開発クラスターが 72 時間アイドル稼働。auto_termination 無効で DBU 消費が前週比 280% 増。AI 起票文を自動生成。',
    tags: ['Databricks', 'DBU', 'コスト異常', 'AI起票', 'ガバナンス'],
    messages: [
      { sender: 'pmo', content: 'DBU 消費量アラート: ALL_PURPOSE_COMPUTE が前週比 280% 増加。推定 $1,633/日。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '原因: cluster-dev-interactive-01 (yamamoto)\n起動: 72時間前、最終アクティビティ: 54時間前\nauto_termination_minutes: 0 (無効)\nStandard_DS5_v2 × 8 workers\n3日間の不要コスト: $1,687 (¥253,050)' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '【AI 自動起票文】\nDatabricks 開発クラスター 72 時間アイドル稼働\nBR-DATA-003 違反: 日次予算 $600 に対し $1,633 (272%)\n\n推奨: 即時停止 + auto_termination 強制ポリシー導入' },
      { sender: 'user', content: 'チケット送信と Slack 通知。再発防止ポリシーも適用して。' },
      { sender: 'agent', agentRole: 'Cost Agent', content: '✅ クラスター停止、チケット DBX-2026-0042 作成\nポリシー提案: autotermination_minutes 最小 30、num_workers 最大 4' },
    ],
  },
  {
    id: 'ds-9',
    title: 'Databricks データ品質ゲート失敗 — 下流影響分析',
    category: 'Databricks',
    icon: <StorageIcon />,
    color: '#ff6b35',
    description: 'Oracle 連携テーブルの品質ゲート失敗 (NULL 率 12.3%)。Unity Catalog リネージで下流 5 テーブルへの影響を分析。',
    tags: ['Databricks', 'Data Quality', 'Unity Catalog', 'リネージ', 'Oracle'],
    messages: [
      { sender: 'pmo', content: '品質ゲート失敗: customer_master — email NULL 率 12.3% (閾値 5%)\nregion_id 参照整合性違反 15件\n下流 3 テーブルがブロック。' },
      { sender: 'user', content: '影響範囲を確認して、暫定対応と根本対応を提案してください。' },
      { sender: 'agent', agentRole: 'Log Agent', content: '根本原因: Oracle DBA が 2/22 に email NOT NULL 制約を外した。\nNULL レコード 147件/1,195件 が Databricks に連携。' },
      { sender: 'agent', agentRole: 'Performance Agent', content: '下流影響 (Unity Catalog リネージ):\ncustomer_master → customer_360 → executive_summary\n                → churn_prediction_features → churn_score_daily\n                → marketing_segments\n5テーブル + 3ビジネスプロセスに影響。' },
      { sender: 'agent', agentRole: 'Log Agent', content: '復旧完了:\n1. ✅ COALESCE で NULL 補完 (暫定)\n2. ✅ 下流 5 テーブル再実行完了\n3. ✅ Data Freshness SLO 全テーブル正常復帰\nDBA チケット DQ-2026-0018 作成済み。' },
    ],
  },
];

const senderStyles: Record<string, { label: string; color: string; bgcolor: string }> = {
  pmo: { label: 'PMO', color: '#102A43', bgcolor: '#DCEEFB' },
  user: { label: 'User', color: '#102A43', bgcolor: '#E3F9E5' },
  agent: { label: 'Agent', color: '#fff', bgcolor: '#2680C2' },
};

export function DemoScenarioBanner() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<DemoScenario | null>(null);

  const handleOpen = (scenario: DemoScenario) => {
    setSelectedScenario(scenario);
    setModalOpen(true);
  };

  return (
    <>
      {/* Banner */}
      <Box
        sx={{
          bgcolor: '#fffbe6',
          borderBottom: '1px solid #ffe58f',
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#ffe58f', borderRadius: 2 },
        }}
      >
        <Chip
          icon={<PlayCircleOutlineIcon />}
          label="デモシナリオ"
          size="small"
          sx={{
            bgcolor: '#fa8c16',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.75rem',
            flexShrink: 0,
            '& .MuiChip-icon': { color: 'white' },
          }}
        />
        {DEMO_SCENARIOS.map(s => (
          <Chip
            key={s.id}
            label={s.title}
            size="small"
            onClick={() => handleOpen(s)}
            sx={{
              cursor: 'pointer',
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.300',
              fontSize: '0.75rem',
              flexShrink: 0,
              '&:hover': { bgcolor: `${s.color}15`, borderColor: s.color },
            }}
          />
        ))}
      </Box>

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
      >
        {selectedScenario && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: selectedScenario.color,
                color: 'white',
                py: 1.5,
              }}
            >
              {selectedScenario.icon}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white' }}>
                  {selectedScenario.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {selectedScenario.category}
                </Typography>
              </Box>
              <IconButton onClick={() => setModalOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {/* Description + Tags */}
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedScenario.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selectedScenario.tags.map(tag => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>

              {/* Chat Messages */}
              <Box sx={{ px: 3, py: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5, display: 'block' }}>
                  会話フロー
                </Typography>
                <Grid container direction="column" spacing={1.5}>
                  {selectedScenario.messages.map((msg, i) => {
                    const style = senderStyles[msg.sender];
                    return (
                      <Grid item key={i}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderLeft: `4px solid ${style.bgcolor === '#E3F9E5' ? '#57AE5B' : style.bgcolor}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={msg.agentRole || style.label}
                              size="small"
                              sx={{
                                bgcolor: style.bgcolor,
                                color: style.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              fontSize: '0.85rem',
                              lineHeight: 1.6,
                            }}
                          >
                            {msg.content}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}
